import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { connect, ConnectedProps } from 'react-redux';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { Footer } from '../../../../../components/atoms/Footer/Footer';
import { Button } from '../../../../../components/atoms/Button/Button';
import { Header } from '../../../../../components/organisms/Header/Header';
import { setApplicationError as setApplicationErrorConnect } from '../../../../../stores/app-state-store';

import { LoadingOverlay } from '../../../LoadingOverlay/LoadingOverlay';
import { ContentBody } from '../../../../../components/atoms/ContentBody/ContentBody';
import { ScreenWrapper } from '../../../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { PageView } from '../../../../../models/analytics';
import { HtmlBlock } from './HtmlBlock';
import { ChromelessPageWrapper } from './LegalTemplate.styles';
import { messages } from './messages';

export type LegalTemplateOwnProps = {
  onBack: () => void;
  documentUrl: string;
  chromeless?: boolean;
  fetchLegal?: (url: string) => Promise<string>;
  ariaLabel?: string;
};

export type LegalTemplateProps = PropsFromRedux & LegalTemplateOwnProps;

const defaultFetchLegal = async (url: string) => {
  const res = await fetch(url, { headers: { Accept: 'text/html' } });

  if (res.status === 200) {
    return res.text();
  }
  throw new Error('Could not fetch legal document');
};

export const LegalTemplatePresenter: React.FC<LegalTemplateProps> = ({
  onBack,
  documentUrl,
  setApplicationError,
  chromeless,
  fetchLegal = defaultFetchLegal,
  ariaLabel,
}) => {
  const [legalDocument, setLegalDocument] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const html = await fetchLegal(documentUrl);
        setLegalDocument(html);
      } catch {
        setApplicationError({
          errorStatus: ErrorStatus.INTERNAL_ERROR,
          errorReason: ErrorReason.REQUEST_FAILED_FETCH_LEGAL_TEXT,
        });
      }
    };

    fetchDocument();
  }, [fetchLegal, documentUrl, setApplicationError]);

  useEffect(() => {
    if (ref.current && legalDocument) {
      ref.current.focus();
    }
  }, [ref, legalDocument]);

  if (!legalDocument) {
    return <LoadingOverlay />;
  }

  if (chromeless) {
    return (
      <ChromelessPageWrapper data-testid="legal-template-wrapper-chromeless">
        <HtmlBlock id="htmlContent" htmlContent={legalDocument} />
      </ChromelessPageWrapper>
    );
  }

  return (
    <ScreenWrapper
      data-testid="legal-template-wrapper"
      role="dialog"
      // making it modal is key to ensure VoiceOver and similar tools stay within the dialog until closed
      aria-modal="true"
      aria-describedby="htmlContent"
      tabIndex={0}
      ref={ref}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <Header onBack={undefined} hideCloseButton pageView={PageView.LEGAL_TEMPLATE_SCREEN} />
      <ContentBody>
        <HtmlBlock id="htmlContent" htmlContent={legalDocument} />
      </ContentBody>
      <Footer>
        <Button color="primary" onClick={onBack} onKeyPress={onBack} variant="contained">
          <FormattedMessage {...messages.close} />
        </Button>
      </Footer>
    </ScreenWrapper>
  );
};

const connector = connect(undefined, {
  setApplicationError: setApplicationErrorConnect,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

export const LegalTemplate = connector(LegalTemplatePresenter);
